import { getKey } from "@redotech/react-util/key";
import { ReturnContext } from "@redotech/redo-customer-portal-shared/contexts/return";
import { useInStoreLocations } from "@redotech/redo-customer-portal-shared/hooks/useReturnConfirmation/use-in-store-locations";
import { useTranslation } from "@redotech/redo-i18n";
import {
  IMPERIAL_DISTANCE_COUNTRIES,
  milesToKm,
} from "@redotech/redo-model/distance-conversion";
import { InteractiveMapLocation } from "@redotech/redo-model/draft-return/draft-return";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoCheckboxCard } from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox-card";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { LoadingRedoAnimation } from "@redotech/redo-web/loading-redo-animation";
import { Text } from "@redotech/redo-web/text";
import {
  APIProvider,
  AdvancedMarker,
  Map,
  Pin,
  useMap,
} from "@vis.gl/react-google-maps";
import { memo, useContext, useEffect, useMemo, useRef, useState } from "react";
import { SettingsContext } from "../../../contexts/settings";
import * as interactiveMap from "./in-store-locations.module.css";

const LocationsModal = memo(function LocationsModal({
  draftReturnId,
  returnId,
}: {
  draftReturnId?: string;
  returnId?: string;
}) {
  const { t } = useTranslation();
  const settings = useContext(SettingsContext);
  const { locationsLoad } = useInStoreLocations(
    draftReturnId,
    returnId,
    settings,
  );

  if (locationsLoad.pending) {
    return <LoadingRedoAnimation />;
  }
  if (locationsLoad.error) {
    return <Text textColor="error">{t("Error loading locations")}</Text>;
  }
  if (!locationsLoad.value) {
    return null;
  }

  return (
    <InteractiveMap
      key={getKey(locationsLoad.value)}
      locations={locationsLoad.value}
    />
  );
});

function formatDistance(
  distanceMiles: string | null | undefined,
  country: string,
): string | null {
  if (!distanceMiles) {
    return null;
  }
  const miles = parseFloat(distanceMiles);
  if (isNaN(miles)) {
    return null;
  }
  if (IMPERIAL_DISTANCE_COUNTRIES.has(country)) {
    return `${miles} miles`;
  }
  return `${milesToKm(miles).toFixed(1)} km`;
}

const LocationsList = memo(function LocationsList({
  locations,
  selectedIndex,
  selectLocation,
  customerCountry: customerCountryProp,
}: {
  locations: InteractiveMapLocation[];
  selectedIndex: number;
  selectLocation: (index: number) => void;
  customerCountry?: string;
}) {
  const { t } = useTranslation();
  const { draftReturn } = useContext(ReturnContext);
  const customerCountry =
    customerCountryProp ??
    draftReturn?.customer.shippingAddress?.country ??
    "US";
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (ref.current) {
      ref.current.children[selectedIndex]?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [selectedIndex]);

  if (!locations.length) {
    return <div>{t("No locations found")}</div>;
  }
  return (
    <div className={interactiveMap.locationsTextContainer} ref={ref}>
      <Flex dir="column" gap="2xl" p="xl">
        {locations.map((location, i) => (
          <RedoCheckboxCard
            checked={selectedIndex === i}
            key={i}
            onClick={() => selectLocation(i)}
            title={`${i + 1}. ${location.name}`}
          >
            <Flex dir="column" gap="xs">
              <Text fontSize="sm" textColor="secondary">
                {(() => {
                  const dist = formatDistance(
                    location.distance,
                    customerCountry,
                  );
                  return dist ? (
                    <span>{t("({{distance}})", { distance: dist })}</span>
                  ) : null;
                })()}
              </Text>
              <Text fontSize="sm" textColor="secondary">
                {location.address.address1}
              </Text>
              {location.address.address2 && (
                <Text fontSize="sm" textColor="secondary">
                  {location.address.address2}
                </Text>
              )}
              <Text fontSize="sm" textColor="secondary">
                {location.address.city}, {location.address.state || ""}{" "}
                {location.address.zip}
              </Text>
            </Flex>
          </RedoCheckboxCard>
        ))}
      </Flex>
    </div>
  );
});

const LocationMap = memo(function LocationMap({
  locations,
  selectedIndex,
  selectLocation,
}: {
  locations: InteractiveMapLocation[];
  selectedIndex: number;
  selectLocation: (index: number) => void;
}) {
  const map = useMap();
  const positions = useMemo(() => {
    return locations.map((location) =>
      location.geo !== undefined
        ? { lat: location.geo!.latitude, lng: location.geo!.longitude }
        : undefined,
    );
  }, [locations]);

  useEffect(() => {
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    if (map && locations[selectedIndex].geo) {
      map.setCenter({
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        lat: locations[selectedIndex].geo!.latitude,
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        lng: locations[selectedIndex].geo!.longitude,
      });
    }
  }, [map, selectedIndex, locations]);

  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  if (locations[selectedIndex].geo === undefined) {
    return null;
  }

  return (
    <Flex className={interactiveMap.mapContainer}>
      <Map
        defaultCenter={positions[0]}
        defaultZoom={12}
        fullscreenControl={false}
        mapId="37a5dbe51230f056"
        mapTypeControl={false}
        streetViewControl={false}
      >
        {positions.map(
          (position, i) =>
            position && (
              <AdvancedMarker
                key={i}
                onClick={() => selectLocation(i)}
                position={position}
                zIndex={i === selectedIndex ? 10_000 : i}
              >
                <Pin borderColor="black" scale={i === selectedIndex ? 1.25 : 1}>
                  <span
                    style={{ fontSize: i === selectedIndex ? "18px" : "14px" }}
                  >
                    {i + 1}
                  </span>
                </Pin>
              </AdvancedMarker>
            ),
        )}
      </Map>
    </Flex>
  );
});

export const InStoreLocationsButton = memo(function InStoreLocationsButton({
  draftReturnId,
  returnId,
}: {
  draftReturnId?: string;
  returnId?: string;
}) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  return (
    <Flex>
      <RedoButton
        hierarchy="secondary"
        onClick={() => setIsOpen(true)}
        text={t("See available locations")}
      />
      <RedoModal
        isOpen={isOpen}
        onModalCloseRequested={() => setIsOpen(false)}
        title={t("Find a store that accepts drop offs")}
      >
        <LocationsModal draftReturnId={draftReturnId} returnId={returnId} />
      </RedoModal>
    </Flex>
  );
});

export const InteractiveMap = memo(function InteractiveMap({
  locations,
  customerCountry,
}: {
  locations: InteractiveMapLocation[];
  customerCountry?: string;
}) {
  const { t } = useTranslation();
  const [selectedIndex, setSelectedIndex] = useState(0);
  if (!locations.length) {
    return <div>{t("No locations found")}</div>;
  }

  return (
    <div className={interactiveMap.locationsContainer}>
      <LocationsList
        customerCountry={customerCountry}
        locations={locations}
        selectedIndex={selectedIndex}
        selectLocation={setSelectedIndex}
      />
      <APIProvider apiKey="AIzaSyBLnX3xDv_dzLIUsaKBZDW4vNJnpimI31M">
        <LocationMap
          locations={locations}
          selectedIndex={selectedIndex}
          selectLocation={setSelectedIndex}
        />
      </APIProvider>
    </div>
  );
});
